import React, { useLayoutEffect, useRef, useState } from 'react';
import ReactEcharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { injectIntl } from 'react-intl';

// ECharts sizes a gauge off the *smaller* of the canvas width/height. The chart
// used to get its size implicitly: no `style` was passed to ReactEcharts, so the
// canvas fell back to echarts-for-react's default 300px height in a 300px-wide
// box — a 300px gauge, which is the size these pixel metrics were drawn for. The
// box itself was a hard 300x175, so on a narrow card (iPad dual-pane, small
// windows) the canvas simply overflowed and was clipped.
//
// Size it from the measured container width instead: full size when there is
// room, proportionally smaller when there is not. At MAX_DIAMETER the geometry
// below reproduces the original rendering exactly.
const MAX_DIAMETER = 300;
const MIN_DIAMETER = 120;
const SIDE_PADDING = 24;

// Radius the hard-coded pixel metrics (band width, fonts, pointer) were authored
// against. Everything scales relative to it, so the proportions always hold —
// and `scale` is 1 (i.e. metrics untouched) whenever the gauge is at full size.
const DESIGN_RADIUS = MAX_DIAMETER / 2;

const TOP_PADDING = 10;
const VALUE_LABEL_SPACE = 70; // room under the arc for the big "0%" readout

const getGeometry = width => {
  const diameter = Math.max(MIN_DIAMETER, Math.min(MAX_DIAMETER, width - SIDE_PADDING));
  const radius = diameter / 2;
  const scale = radius / DESIGN_RADIUS;
  return {
    radius,
    scale,
    height: Math.round(radius + TOP_PADDING + VALUE_LABEL_SPACE * scale)
  };
};

const buildOption = (score, label, { radius, height, scale }) => ({
  title: {
    show: false
  },
  animationDuration: 1200,
  animationDurationUpdate: 1200,
  grid: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },
  tooltip: {
    formatter: '{b}: {c}'
  },
  legend: {
    show: false
  },
  toolbox: {},
  series: [
    {
      name: label,
      type: 'gauge',
      min: 0,
      max: 80,
      splitNumber: 4,
      startAngle: 180,
      endAngle: 0,
      radius: radius,
      center: ['50%', `${(((radius + TOP_PADDING) / height) * 100).toFixed(1)}%`],
      data: [
        {
          value: isNaN(parseInt(score)) ? 0 : parseInt(score),
          name: label
        }
      ],
      axisLine: {
        lineStyle: {
          color: [
            [
              1,
              new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                {
                  offset: 0,
                  color: '#ff0000'
                },
                {
                  offset: 0.1875,
                  color: '#ffa600'
                },
                {
                  offset: 0.475,
                  color: '#3eda7d'
                }
              ])
            ]
          ],
          width: 43 * scale
        }
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        show: true,
        distance: 20 * scale,
        formatter: v => `${v}%`,
        color: '#d6d6d6',
        fontWeight: 'normal',
        fontSize: 10 * scale,
        shadowBlur: 0
      },
      splitLine: {
        show: false
      },
      pointer: {
        length: '45%',
        width: 6 * scale
      },
      itemStyle: {
        color: '#3966f8',
        shadowBlur: 0
      },
      title: {
        show: false,
        offsetCenter: [0, 0],
        textStyle: {
          color: '#757575',
          fontSize: 13 * scale
        }
      },
      detail: {
        show: true,
        borderWidth: 0,
        borderColor: '#ccc',
        offsetCenter: [0, 40 * scale],
        valueAnimation: true,
        formatter: value => `${value > 80 ? '>80' : value}%`,
        textStyle: {
          color: '#3966f8',
          fontSize: 25 * scale
        },
        height: '31%',
        width: '25%',
        rich: {}
      },
      tooltips: {
        formatter: '{a} <br/>{b} : {c}%'
      }
    }
  ]
});

const SavingRatioGauge = ({ intl, score }) => {
  const holderRef = useRef(null);
  const [width, setWidth] = useState(MAX_DIAMETER + SIDE_PADDING);

  useLayoutEffect(() => {
    const holder = holderRef.current;
    if (!holder) return undefined;

    const measure = () => setWidth(holder.clientWidth || MAX_DIAMETER + SIDE_PADDING);

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(holder);
    return () => observer.disconnect();
  }, []);

  const geometry = getGeometry(width);

  return (
    <div
      ref={holderRef}
      id="canvas-holder"
      className="Outer"
      style={{ width: '100%', height: geometry.height, margin: 'auto' }}
    >
      <ReactEcharts
        option={buildOption(score, intl.formatMessage({ id: 'Saving Ratio' }), geometry)}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default injectIntl(SavingRatioGauge);
